var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { makeObservable, observable, runInAction } from 'mobx';
import { TravelInsuranceService, } from '@data/api/services/travel-insurance';
import { persistentPropertyStorage } from '../PersistentPropertyStorage';
const CACHE_DURATION_MS = 45 * 60 * 1000; // 45 minutes
export default class TravelInsuranceStore {
    constructor() {
        Object.defineProperty(this, "isFetching", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "lastFetchTimestamp", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "lastRequestSignature", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "offers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "pendingRequest", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        makeObservable(this);
    }
    get storageKey() {
        return 'travelInsuranceOffers';
    }
    clear() {
        runInAction(() => {
            this.offers = [];
            this.save();
        });
        this.invalidateCache();
    }
    fetchOffers(payload) {
        return __awaiter(this, void 0, void 0, function* () {
            const requestSignature = JSON.stringify(payload);
            const now = Date.now();
            // Check if we have cached data that's still fresh
            const isCacheValid = this.lastRequestSignature === requestSignature &&
                now - this.lastFetchTimestamp < CACHE_DURATION_MS &&
                this.offers.length > 0;
            if (isCacheValid) {
                // Return cached data without making API call
                return this.offers;
            }
            // Check if there's already a pending request for the same data
            const currentPendingRequest = this.pendingRequest;
            if (currentPendingRequest &&
                this.lastRequestSignature === requestSignature) {
                return yield currentPendingRequest;
            }
            // Create new request
            runInAction(() => {
                this.isFetching = true;
            });
            this.pendingRequest = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const result = yield TravelInsuranceService.getOffers(payload);
                    runInAction(() => {
                        var _a;
                        this.offers = (_a = result.offers) !== null && _a !== void 0 ? _a : [];
                        this.isFetching = false;
                        this.lastRequestSignature = requestSignature;
                        this.lastFetchTimestamp = now;
                    });
                    return result.offers;
                }
                catch (error) {
                    runInAction(() => {
                        this.isFetching = false;
                    });
                    return [];
                }
                finally {
                    this.pendingRequest = null;
                }
            }))();
            return yield this.pendingRequest;
        });
    }
    // Invalidate cache - useful when property changes or session expires
    invalidateCache() {
        this.lastRequestSignature = null;
        this.lastFetchTimestamp = 0;
        this.pendingRequest = null;
    }
    load() {
        var _a;
        const stored = persistentPropertyStorage.getItem(this.storageKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                this.offers = (_a = parsed.offers) !== null && _a !== void 0 ? _a : [];
            }
            catch (e) {
                console.error('Failed to load travel insurance offers', e);
                this.offers = [];
            }
        }
    }
    save() {
        const data = {
            offers: this.offers,
        };
        persistentPropertyStorage.setItem(this.storageKey, JSON.stringify(data));
    }
}
__decorate([
    observable,
    __metadata("design:type", Object)
], TravelInsuranceStore.prototype, "isFetching", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], TravelInsuranceStore.prototype, "offers", void 0);
